import React from "react"
import { SSEWrapper, RenderChildParameters } from "./SSEWrapper"
import { TranspayrentPaymentForm } from "./TranspayrentPaymentForm"
import { StripeCardPaymentForm } from "./StripeCardPaymentForm"
import { CheckoutOrder } from "../../../models/order"
import { CheckoutFormType } from "../../../models/checkout-form"
import { AddressType } from "../../../models/address"
import { AdyenCardPaymentForm } from "./AdyenCardPaymentForm"
import { EpayCardPaymentForm } from "./EpayCardPaymentForm"
import {
  AdyenPaymentMethod,
  NextActionType,
} from "../../../models/payment-methods"
import AdyenCardSCA from "./AdyenCardSCA"
import { getAdyenCountryCode } from "../../../util/util"
import { captureException } from "../../../services/exception-logger"

type Props = {
  nextAction: NextActionType
  adyenContext?: AdyenPaymentMethod
  hide: () => void
  showPaymentFailedScreen: (
    errorMessage?: string,
    providerName?: string
  ) => void
  showPaymentSucceededScreen: () => void
  order: CheckoutOrder
  checkoutFormValues: CheckoutFormType
  deliveryAddress: AddressType | undefined
  isSca: boolean
  countryCode: string
}

export const CardPaymentForm = ({
  nextAction,
  hide,
  showPaymentFailedScreen,
  showPaymentSucceededScreen,
  order,
  checkoutFormValues,
  deliveryAddress,
  adyenContext,
  isSca,
  countryCode,
}: Props) => {
  const renderChild = ({
    startTimeout,
    sseSubscriber,
    ...args
  }: RenderChildParameters) => {
    const {
      phonenumberPrefix,
      address,
      city,
      email,
      firstName,
      floor,
      lastName,
      phoneNumber,
      postal,
      to,
    } = checkoutFormValues

    switch (nextAction.providerName) {
      case "transpayrent":
        return (
          <TranspayrentPaymentForm
            {...args}
            hide={hide}
            order={order}
            showPaymentFailedScreen={showPaymentFailedScreen}
            transpayrentGateway={isSca ? "creditcard_sca" : "creditcard"}
            paymentProvider={{
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              ...(({ providerName, parameters, ...rest }) => rest)(nextAction),
              parameters: {
                phonenumberPrefix,
                ...(deliveryAddress && deliveryAddress),
                ...(address && { address: address }),
                ...(city && { city: city }),
                ...(email && { email: email }),
                ...(firstName && { firstName: firstName }),
                ...(floor && { floor: floor }),
                ...(lastName && { lastName: lastName }),
                ...(phoneNumber && { phoneNumber: phoneNumber }),
                ...(postal && { postal: postal }),
                ...(to && { to: to }),
                ...nextAction.parameters,
              },
            }}
            showPaymentSucceededScreen={showPaymentSucceededScreen}
            sseSubscriber={sseSubscriber}
            startTimeout={() => {
              startTimeout("Transpayrent Card", () => {
                sseSubscriber?.current?.close()
              })
            }}
          />
        )
      case "epay":
        return (
          <EpayCardPaymentForm
            {...args}
            hide={hide}
            order={order}
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            nextAction={(({ providerName, ...rest }) => rest)(nextAction)}
            sseSubscriber={sseSubscriber}
            showPaymentFailedScreen={showPaymentFailedScreen}
            showPaymentSucceededScreen={showPaymentSucceededScreen}
            startTimeout={(preCancelAction) => {
              startTimeout("ePay Card", () => {
                return preCancelAction()
              })
            }}
          />
        )
      case "stripe":
        return (
          <StripeCardPaymentForm
            {...args}
            hide={hide}
            order={order}
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            nextAction={(({ providerName, ...rest }) => rest)(nextAction)}
            sseSubscriber={sseSubscriber}
            showPaymentFailedScreen={showPaymentFailedScreen}
            showPaymentSucceededScreen={showPaymentSucceededScreen}
            startTimeout={(preCancelAction) => {
              startTimeout("Stripe Card", () => {
                return preCancelAction()
              })
            }}
          />
        )
      case "adyen":
      case "adyen_custom":
        return (
          <>
            {isSca ? (
              <AdyenCardSCA
                action={nextAction.action}
                client_key={nextAction.client_key}
                countryCode={getAdyenCountryCode(countryCode)}
                payment_details_url={nextAction.payment_details_url}
                showPaymentFailedScreen={showPaymentFailedScreen}
              />
            ) : (
              <AdyenCardPaymentForm
                {...args}
                hide={hide}
                order={order}
                adyenContext={adyenContext!}
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                nextAction={(({ providerName, ...rest }) => rest)(nextAction)}
                sseSubscriber={sseSubscriber}
                showPaymentFailedScreen={showPaymentFailedScreen}
                showPaymentSucceededScreen={showPaymentSucceededScreen}
                startTimeout={(preCancelAction) => {
                  startTimeout("Adyen Card", () => {
                    return preCancelAction()
                  })
                }}
              />
            )}
          </>
        )
      default: {
        captureException(
          new Error(
            `Unknown payment provider ${JSON.stringify(nextAction.providerName)}`
          ),
          {
            type: "business_logic",
            category: "unsupported-operation",
            context: {
              errorSource: "CardPaymentForm - renderChild",
              providerName: nextAction.providerName,
            },
          }
        )
        return <></>
      }
    }
  }

  return (
    <SSEWrapper
      showPaymentFailedScreen={showPaymentFailedScreen}
      showPaymentSucceededScreen={showPaymentSucceededScreen}
      order={order}
      renderChild={renderChild}
    />
  )
}
